import moment from 'moment';
import { CampaignResponse, Conditions, Result } from '@models/campaign';
import { ItemResponse } from '@models/order/items/response';
import { GROUP_TYPES } from 'src/enums/group-types';
import {
    ApplyContext,
    ConflictItem,
    FetchContext,
    FetchParams,
    OrderContext,
    SubmitError,
    ValidationError,
} from '../types';
import { CampaignStrategy } from './campaign-strategy';
import { getBenefitCalculator, isBonusBenefit } from '../benefits';

const CONDITION_FIELD = 'ATINGIR_COTA';
const PAYMENT_CONDITION_BONIFICACAO = '100';

const getConditionItems = (campaign: CampaignResponse): Conditions[] =>
    campaign.condicoes.filter((c) => c.campo === CONDITION_FIELD);

// Preço unitário usado para medir a meta: o valor ANTES do desconto. Usar o
// `valor` (já descontado) faz o desconto da própria campanha reduzir o atingido
// e derrubar a meta ao aumentar a quantidade.
export const getMetaUnitPrice = (item: ItemResponse | undefined): number => {
    if (!item) return 0;
    const semDesconto = Number(item.valorsemdesconto) || 0;
    if (semDesconto > 0) return semDesconto;
    return typeof item.valor === 'number' ? item.valor : Number(item.valor) || 0;
};

export const getMetaLineValue = (item: ItemResponse | undefined): number => {
    if (!item) return 0;
    const quantity = Number(item.quantity) || 0;
    const presentation = Number(item.quantityPresentation) || 1;
    return getMetaUnitPrice(item) * quantity * presentation;
};

const lineValue = getMetaLineValue;

const round2 = (n: number): number => Math.round(n * 100) / 100;

interface ConditionEvaluation {
    minTotal: number;
    achievedTotal: number;
    isMet: boolean;
}

const evaluateCondition = (
    campaign: CampaignResponse,
    orderItems: ItemResponse[],
): ConditionEvaluation => {
    const conditions = getConditionItems(campaign);
    let minTotal = 0;
    let achievedTotal = 0;
    let allConditionItemsHaveQuantity = true;

    for (const cond of conditions) {
        const item = orderItems.find(
            (i) => i.number?.toLowerCase() === cond.numeroItem?.toLowerCase(),
        );
        const gross = Number(item?.valorGross) || 0;
        const percentual = Number(cond.valor) || 0;
        const minItem = gross * (1 + percentual / 100);
        minTotal += minItem;
        achievedTotal += lineValue(item);

        // Cada item de condição deve ter pelo menos quantity > 0,
        // mesmo que o valor mínimo calculado seja 0.
        const qty = Number(item?.quantity) || 0;
        if (qty <= 0) {
            allConditionItemsHaveQuantity = false;
        }
    }

    const valueMet = round2(minTotal) === 0
        ? round2(achievedTotal) > 0
        : round2(achievedTotal) >= round2(minTotal);

    return {
        minTotal: round2(minTotal),
        achievedTotal: round2(achievedTotal),
        isMet: valueMet && allConditionItemsHaveQuantity,
    };
};

export class DescontoAtingirMetaStrategy implements CampaignStrategy {
    readonly tipo = 'DESCONTO_ATINGIR_META' as const;

    isAvailableForOrder(
        _campaign: CampaignResponse,
        context: OrderContext,
    ): boolean {
        if (context.isMarketingBonification) return false;
        return context.orderTypeId === GROUP_TYPES.S1;
    }

    validateEligibility(
        campaign: CampaignResponse,
        availableItems: ItemResponse[],
    ): ValidationError[] {
        const errors: ValidationError[] = [];

        for (const cond of getConditionItems(campaign)) {
            const item = availableItems.find(
                (i) => i.number === cond.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'CONDITION_ITEM_MISSING',
                    message: `SKU ${cond.numeroItem} (condição) não disponível`,
                    numeroItem: cond.numeroItem,
                });
            }
        }

        for (const result of campaign.resultados) {
            if (!result.numeroItem) continue;
            const item = availableItems.find(
                (i) => i.number === result.numeroItem,
            );
            if (!item) {
                errors.push({
                    code: 'RESULT_ITEM_MISSING',
                    message: `SKU ${result.numeroItem} (resultado) não disponível`,
                    numeroItem: result.numeroItem,
                });
            }
        }

        return errors;
    }

    detectConflicts(
        campaign: CampaignResponse,
        otherSelected: CampaignResponse[],
    ): ConflictItem[] {
        const conflicts: ConflictItem[] = [];
        const ownNumbers = new Set<string>([
            ...getConditionItems(campaign).map((c) => c.numeroItem),
            ...campaign.resultados.map((r) => r.numeroItem).filter(Boolean),
        ]);

        for (const other of otherSelected) {
            if (other.id === campaign.id) continue;
            const otherNumbers = [
                ...other.condicoes
                    .filter((c) => c.campo === 'QUANTIDADE_ITEM' || c.campo === CONDITION_FIELD)
                    .map((c) => c.numeroItem),
                ...other.resultados.map((r) => r.numeroItem).filter(Boolean),
            ];

            for (const n of otherNumbers) {
                if (ownNumbers.has(n)) {
                    conflicts.push({
                        numeroItem: n,
                        campaignIds: [campaign.id, other.id],
                    });
                }
            }
        }

        return conflicts;
    }

    onEnable(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        context: ApplyContext,
    ): ItemResponse[] {
        if (isBonificationScreen(context)) return orderItems;
        const conditions = getConditionItems(campaign);
        if (conditions.length === 0) return orderItems;

        return orderItems.map((item) => {
            const cond = conditions.find(
                (c) => c.numeroItem?.toLowerCase() === item.number?.toLowerCase(),
            );
            if (!cond) return item;

            const gross = Number(item.valorGross) || 0;
            const percentual = Number(cond.valor) || 0;
            const presentation = Number(item.quantityPresentation) || 1;
            const lineUnit = getMetaUnitPrice(item) * presentation;

            const minItem = gross * (1 + percentual / 100);
            if (gross <= 0 || lineUnit <= 0) return item;

            const qtyMin = Math.ceil(minItem / lineUnit);
            const currentQty = Number(item.quantity) || 0;
            const currentLine = currentQty * lineUnit;

            if (currentLine >= minItem) return item;

            return { ...item, quantity: qtyMin };
        });
    }

    applyResultToItem(
        item: ItemResponse,
        result: Result,
        campaign: CampaignResponse,
        context: ApplyContext,
    ): ItemResponse {
        if (isBonificationScreen(context)) {
            // Tela de bonificação: só os resultados com bonificação calculada
            // (meta batida na venda) viram item da campanha.
            const line = computeAtingirMetaBonusLines(
                campaign,
                context.saleItems ?? [],
            ).find((l) => l.result === result);
            if (!line) return item;
            return applyBonusLine(item, line, campaign);
        }

        return {
            ...item,
            campaign,
            result,
        };
    }

    recalculateOnQuantityChange(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        context: ApplyContext,
    ): ItemResponse[] {
        if (isBonificationScreen(context)) {
            return recalculateBonification(campaign, orderItems, context);
        }

        const { isMet } = evaluateCondition(campaign, orderItems);
        const resultByNumero = new Map<string, Result>();
        for (const r of campaign.resultados) {
            if (!r.numeroItem) continue;
            resultByNumero.set(r.numeroItem.toLowerCase(), r);
        }

        return orderItems.map((item) => {
            const result = resultByNumero.get((item.number ?? '').toLowerCase());
            if (!result) return item;
            if (item.campaign && item.campaign.id !== campaign.id) return item;

            if (!isMet) {
                if (item.discount === 0 && !item.result) return item;
                return { ...item, discount: 0 };
            }

            const gross = Number(item.valorGross) || 0;
            const percentualCota = Number(result.percentualCota) || 0;
            const meta = gross * (1 + percentualCota / 100);
            const achieved = lineValue(item);
            // O desconto aplicado na linha de venda depende do tipo de benefício
            // da linha (Fase 2). Tipos só-bonificação retornam desconto 0; tipos
            // legados (result.tipo nulo) caem em DESCONTO (retrocompatível).
            const outcome = getBenefitCalculator(result.tipo).calculate(
                result,
                item,
            );
            const desconto = -(outcome.desconto || 0);

            const itemMet =
                round2(meta) === 0
                    ? round2(achieved) > 0
                    : round2(achieved) >= round2(meta);

            if (itemMet) {
                return {
                    ...item,
                    discount: desconto,
                    result,
                    campaign,
                };
            }

            return { ...item, discount: 0, result, campaign };
        });
    }

    validateForSubmit(
        campaign: CampaignResponse,
        orderItems: ItemResponse[],
        context: ApplyContext,
    ): SubmitError[] {
        // A meta é validada no pedido de venda; na bonificação não há o que checar.
        if (isBonificationScreen(context)) return [];

        const { isMet, achievedTotal, minTotal } = evaluateCondition(
            campaign,
            orderItems,
        );
        if (isMet) return [];

        // Identifica itens de condição sem quantidade para mensagem mais clara
        const conditions = getConditionItems(campaign);
        const itemsWithoutQty = conditions.filter((cond) => {
            const item = orderItems.find(
                (i) => i.number?.toLowerCase() === cond.numeroItem?.toLowerCase(),
            );
            return !item || Number(item.quantity) <= 0;
        });

        if (itemsWithoutQty.length > 0 && round2(minTotal) === 0) {
            const itemNumbers = itemsWithoutQty.map((c) => c.numeroItem).join(', ');
            return [
                {
                    code: 'CONDITION_NOT_MET',
                    message: `Campanha "${campaign.nome}": todos os itens de condição devem ter quantidade. Itens pendentes: ${itemNumbers}.`,
                    campaignId: campaign.id,
                },
            ];
        }

        const missing = round2(minTotal - achievedTotal);
        return [
            {
                code: 'CONDITION_NOT_MET',
                message: `Campanha "${campaign.nome}": atinja R$ ${minTotal.toFixed(2)} em itens da condição. Faltam R$ ${missing.toFixed(2)}.`,
                campaignId: campaign.id,
            },
        ];
    }

    requiresExtraFetchParams(
        _campaign: CampaignResponse,
        context: FetchContext,
    ): Partial<FetchParams> {
        const mes = context.orderDate
            ? moment(context.orderDate).month() + 1
            : moment().month() + 1;
        return { mes };
    }

    // Venda: avalia a meta e aplica o desconto. Bonificação ('100'): preenche
    // os itens de resultado com a quantidade bonificada.
    acceptsPaymentCondition(_paymentConditionId: string): boolean {
        return true;
    }
}

export const evaluateAtingirMetaCondition = evaluateCondition;
export const getAtingirMetaConditionItems = getConditionItems;

export interface AtingirMetaBonusLine {
    numeroItem: string;
    description: string;
    /** Quantidade grátis, na unidade de `bonusMultiplo`. */
    bonusQty: number;
    /** Múltiplo/apresentação da linha grátis. */
    bonusMultiplo: number;
    /** Preço unitário base do item (para montar a linha grátis). */
    valor: number;
    campanhaId: number;
    result: Result;
}

/**
 * Fase 2: calcula as linhas de bonificação (grátis) de uma campanha
 * DESCONTO_ATINGIR_META a partir dos itens do pedido de VENDA.
 *
 * Só gera linha quando a condição global É atendida E o item do resultado bate
 * a própria meta individual — o mesmo gate do desconto. A linha grátis NÃO
 * entra no pedido de venda: é exibida como prévia no popover da meta e
 * materializada no pedido de bonificação (tela seguinte), como nas campanhas
 * de bonificação fixa/proporcional.
 */
export const computeAtingirMetaBonusLines = (
    campaign: CampaignResponse,
    orderItems: ItemResponse[],
): AtingirMetaBonusLine[] => {
    const { isMet } = evaluateCondition(campaign, orderItems);
    if (!isMet) return [];

    const lines: AtingirMetaBonusLine[] = [];
    for (const result of campaign.resultados ?? []) {
        if (!result.numeroItem) continue;
        const item = orderItems.find(
            (i) =>
                (i.number ?? '').toLowerCase() ===
                result.numeroItem.toLowerCase(),
        );
        if (!item) continue;

        const outcome = getBenefitCalculator(result.tipo).calculate(
            result,
            item,
        );
        // Linhas de bonificação entram mesmo com quantidade calculada 0: o item
        // continua aparecendo na tela de bonificação com a quantidade da regra.
        if (!isBonusBenefit(result.tipo)) continue;

        const gross = Number(item.valorGross) || 0;
        const percentualCota = Number(result.percentualCota) || 0;
        const meta = gross * (1 + percentualCota / 100);
        const achieved = lineValue(item);
        const itemMet =
            round2(meta) === 0
                ? round2(achieved) > 0
                : round2(achieved) >= round2(meta);
        if (!itemMet) continue;

        const valor =
            typeof item.valor === 'number'
                ? item.valor
                : Number(item.valor) || 0;

        lines.push({
            numeroItem: result.numeroItem,
            description: item.description ?? result.numeroItem,
            bonusQty: outcome.bonusQty,
            bonusMultiplo: outcome.bonusMultiplo,
            valor,
            campanhaId: campaign.id,
            result,
        });
    }
    return lines;
};

// Meta batida e campanha com alguma linha de bonificação: o pedido segue para a
// tela de bonificação, mesmo que a quantidade calculada seja 0.
export const hasAtingirMetaBonusBenefit = (
    campaign: CampaignResponse,
    orderItems: ItemResponse[],
): boolean =>
    evaluateCondition(campaign, orderItems).isMet &&
    (campaign.resultados ?? []).some((r) => isBonusBenefit(r.tipo));

const isBonificationScreen = (context: ApplyContext | undefined): boolean =>
    context?.paymentConditionId === PAYMENT_CONDITION_BONIFICACAO;

// Preenche o item da tela de bonificação com a quantidade calculada. O usuário
// pode bonificar ACIMA do calculado, nunca abaixo: `max` carrega o piso (mesma
// convenção de BonificacaoProporcionalStrategy, lida pela coluna de quantidade).
// A apresentação vem da linha: fixa em unidades avulsas (1), proporcional em
// caixas da apresentação do próprio item.
const applyBonusLine = (
    item: ItemResponse,
    line: AtingirMetaBonusLine,
    campaign: CampaignResponse,
): ItemResponse => {
    const quantity = Math.max(Number(item.quantity) || 0, line.bonusQty);
    const quantityPresentation = line.bonusMultiplo;

    if (
        item.campaign?.id === campaign.id &&
        item.result?.numeroItem === line.result.numeroItem &&
        item.quantity === quantity &&
        item.max === line.bonusQty &&
        item.quantityPresentation === quantityPresentation
    ) {
        return item;
    }

    return {
        ...item,
        campaign,
        result: line.result,
        condition: undefined,
        discount: 0,
        quantity,
        max: line.bonusQty,
        quantityPresentation,
    };
};

const recalculateBonification = (
    campaign: CampaignResponse,
    orderItems: ItemResponse[],
    context: ApplyContext,
): ItemResponse[] => {
    // Sem os itens da venda (ex.: F5 direto na URL de bonificação) não há como
    // reavaliar a meta: preserva o estado atual.
    if (!context.saleItems) return orderItems;

    const lineByNumero = new Map<string, AtingirMetaBonusLine>();
    for (const line of computeAtingirMetaBonusLines(campaign, context.saleItems)) {
        lineByNumero.set(line.numeroItem.toLowerCase(), line);
    }
    if (lineByNumero.size === 0) return orderItems;

    return orderItems.map((item) => {
        const line = lineByNumero.get((item.number ?? '').toLowerCase());
        if (!line) return item;
        if (item.campaign && item.campaign.id !== campaign.id) return item;
        return applyBonusLine(item, line, campaign);
    });
};
